import { Tier } from '@mondaygpt/constants'
import { Offering, Package } from '@revenuecat/purchases-js'
import { PLAN_FEATURES } from '../constants'

// `BillingPeriod` lives inline in `PlanPicker.tsx` and is re-exported through
// the billing module barrel; redeclaring it here avoids a circular import
// (this util is consumed by both PlanPicker and the new DarkPlanPicker).
export type BillingPeriod = 'monthly' | 'yearly'

/**
 * Display config for the plans Taka offers, plus the helpers used to map them
 * to RevenueCat packages and format prices.
 *
 * Shared between the legacy `PlanPicker` and the new `DarkPlanPicker` so the
 * package mapping + price math stay in one place. Visual rendering stays
 * divergent — only the data + helpers live here.
 */

export interface PlanConfig {
  tier: Tier.Pro | Tier.Business
  name: string
  monthlyPrice: string
  yearlyPrice: string
  yearlyTotal: string
  /** Dollar savings of a year prepaid vs 12 months of monthly — the price frame. */
  annualSavings: string
  /** One-time top-up bonus granted at the first paid annual charge. Display
      only — the actual grant reads `Products.annualBonusCredits` server-side,
      so changes must land in both places. */
  annualBonusCredits: number
  /** Retail value of the bonus at top-up pack pricing — the "(a $25 value)" tag. */
  annualBonusValue: string
  credits: number
  seats: number
  features: string[]
  highlighted?: boolean
}

export const PLANS_CONFIG: PlanConfig[] = [
  {
    tier: Tier.Pro,
    name: 'Pro',
    monthlyPrice: '$19',
    yearlyPrice: '$15',
    yearlyTotal: '$180',
    annualSavings: '$48',
    annualBonusCredits: 200,
    annualBonusValue: '$25',
    ...PLAN_FEATURES[Tier.Pro],
  },
  {
    tier: Tier.Business,
    name: 'Business',
    monthlyPrice: '$49',
    yearlyPrice: '$40',
    yearlyTotal: '$480',
    annualSavings: '$108',
    annualBonusCredits: 500,
    annualBonusValue: '$50',
    ...PLAN_FEATURES[Tier.Business],
    highlighted: true,
  },
]

const RC_PACKAGE_MAP: Record<string, { tier: Tier; period: BillingPeriod }> = {
  $rc_monthly: { tier: Tier.Pro, period: 'monthly' },
  $rc_annual: { tier: Tier.Pro, period: 'yearly' },
  biz_monthly: { tier: Tier.Business, period: 'monthly' },
  biz_annual: { tier: Tier.Business, period: 'yearly' },
}

export function getPackageForPlan(
  offering: Offering | null,
  tier: Tier,
  period: BillingPeriod
): Package | null {
  if (!offering) return null
  for (const pkg of offering.availablePackages) {
    const mapping = RC_PACKAGE_MAP[pkg.identifier]
    if (mapping && mapping.tier === tier && mapping.period === period) return pkg
  }
  if (tier === Tier.Pro && period === 'monthly') return offering.monthly ?? null
  if (tier === Tier.Pro && period === 'yearly') return offering.annual ?? null
  return null
}

function formatDollars(micros: number): string {
  const dollars = micros / 1_000_000
  return Number.isInteger(dollars) ? `$${dollars}` : `$${dollars.toFixed(2)}`
}

function getIntroMicros(pkg: Package | null): number | undefined {
  // Trial phases carry a null price; only a real intro amount counts.
  return pkg?.webBillingProduct?.introPricePhase?.price?.amountMicros ?? undefined
}

/** True when the package carries introductory (promo offering) pricing. */
export function hasIntroDiscount(pkg: Package | null): boolean {
  return getIntroMicros(pkg) !== undefined
}

/**
 * True when the package still carries its free-trial phase FOR THIS CUSTOMER.
 * RevenueCat evaluates trial eligibility per customer server-side and strips
 * the phase from the offering when the customer isn't eligible (e.g. any past
 * trial start counts as a purchase) — so trial copy must key off this, never
 * off the billing period alone.
 */
export function hasFreeTrial(pkg: Package | null): boolean {
  return pkg?.webBillingProduct?.freeTrialPhase != null
}

/**
 * Offering-level trial eligibility for paywall copy. Distinguishes "RC
 * stripped the trial for this customer" (packages present, none carries the
 * phase → false) from "we don't know" (no offering / no packages, e.g. RC
 * fetch failed and the paywall is on config-fallback prices → true, keeping
 * the historical trial copy rather than under-selling eligible customers).
 */
export function offeringHasFreeTrial(offering: Offering | null): boolean {
  const packages = offering?.availablePackages ?? []
  return packages.length === 0 || packages.some(pkg => hasFreeTrial(pkg))
}

/** Per-month display price; prefers the introductory (promo) price when present. */
export function formatMonthlyPrice(
  pkg: Package | null,
  fallback: string,
  period: BillingPeriod
): string {
  if (!pkg) return fallback
  const micros = getIntroMicros(pkg) ?? pkg.webBillingProduct?.price?.amountMicros
  if (!micros) return fallback
  return formatDollars(period === 'yearly' ? micros / 12 : micros)
}

/** Per-month base (non-intro) price — the strikethrough anchor when a promo applies. */
export function formatBaseMonthlyPrice(
  pkg: Package | null,
  fallback: string,
  period: BillingPeriod
): string {
  if (!pkg) return fallback
  const micros = pkg.webBillingProduct?.price?.amountMicros
  if (!micros) return fallback
  return formatDollars(period === 'yearly' ? micros / 12 : micros)
}

/** Yearly total; prefers the introductory (promo) price when present. */
export function formatYearlyTotal(pkg: Package | null, fallback: string): string {
  if (!pkg) return fallback
  const micros = getIntroMicros(pkg) ?? pkg.webBillingProduct?.price?.amountMicros
  if (!micros) return fallback
  return formatDollars(micros)
}

/** Yearly base (non-intro) total — the "then $X/yr" anchor when a promo applies. */
export function formatBaseYearlyTotal(pkg: Package | null, fallback: string): string {
  if (!pkg) return fallback
  const micros = pkg.webBillingProduct?.price?.amountMicros
  if (!micros) return fallback
  return formatDollars(micros)
}
